import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  FolderOpen, GraduationCap, MessageSquare, Handshake, Mail,
  MailOpen, Plus, Pencil, Trash2, X, Eye, EyeOff, Briefcase, FileText,
  Save, Globe, Phone, AtSign, MapPin, Palette, Instagram, Youtube, Check,
  Clock, Calendar, UserPlus, Users, Shield, CreditCard, Filter, MessageCircle, Star
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useSettingsUpdater, useSettings } from "@/contexts/SettingsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getToken() { return localStorage.getItem("admin_token"); }
function authHeaders() { return { Authorization: `Bearer ${getToken()}` }; }

// --- Inspiring Quotes ---
const quotes = [
  "النجاح ليس نهاية الطريق، بل بدايته",
  "كل يوم هو فرصة جديدة للإبداع",
  "الرؤية بلا تنفيذ مجرد حلم",
  "أفضل وقت للبدء هو الآن",
  "الإبداع هو أن ترى ما يراه الجميع وتفكر بما لم يفكر به أحد",
  "جودة العمل هي أفضل خطة عمل",
  "المستقبل يصنعه من يؤمن بجمال أحلامه",
];

// --- Welcome Banner ---
function WelcomeBanner() {
  const settings = useSettings();
  const [time, setTime] = useState(new Date());
  const adminName = localStorage.getItem("admin_name") || "المشرف";
  const quote = quotes[Math.floor(new Date().getDate() % quotes.length)];

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const gregorianDate = new Intl.DateTimeFormat("ar-SA", {
    weekday: "long", year: "numeric", month: "long", day: "numeric"
  }).format(time);

  let hijriDate = "";
  try {
    hijriDate = new Intl.DateTimeFormat("ar-SA-u-ca-islamic", {
      day: "numeric", month: "long", year: "numeric"
    }).format(time);
  } catch { hijriDate = ""; }

  const timeStr = new Intl.DateTimeFormat("ar-SA", {
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true
  }).format(time);

  return (
    <div className="relative overflow-hidden rounded-2xl mb-8 border border-white/5" data-testid="welcome-banner"
      style={{ background: "linear-gradient(135deg, rgba(22,22,26,0.9) 0%, rgba(15,15,20,0.95) 100%)" }}>
      {/* Background glow */}
      <div className="absolute top-0 left-0 w-64 h-64 rounded-full blur-[100px] opacity-20"
        style={{ background: `hsl(var(--primary))` }} />
      <div className="absolute bottom-0 right-0 w-48 h-48 rounded-full blur-[80px] opacity-15"
        style={{ background: `hsl(var(--gradient-end))` }} />

      <div className="relative z-10 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        {/* Right - Welcome */}
        <div className="flex-1">
          <p className="text-zinc-500 text-sm mb-1">مرحباً بك في</p>
          <h2 className="text-2xl md:text-3xl font-bold font-['Zain'] mb-2"
            style={{
              background: `linear-gradient(135deg, hsl(var(--primary)), hsl(var(--gradient-end)))`,
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent"
            }}>
            {settings.site_name || "رؤية"}
          </h2>
          <p className="text-white text-lg font-medium mb-1">{adminName}</p>
          <p className="text-zinc-500 text-sm italic">"&nbsp;{quote}&nbsp;"</p>
        </div>

        {/* Left - Date & Time */}
        <div className="text-left md:text-left space-y-2 shrink-0">
          <div className="flex items-center gap-2 text-zinc-400">
            <Clock size={15} className="text-primary" />
            <span className="text-xl font-mono font-bold text-white" dir="ltr">{timeStr}</span>
          </div>
          <div className="flex items-center gap-2 text-zinc-400">
            <Calendar size={14} className="text-primary" />
            <span className="text-sm">{gregorianDate}</span>
          </div>
          {hijriDate && (
            <div className="flex items-center gap-2 text-zinc-500">
              <Calendar size={14} className="text-zinc-600" />
              <span className="text-xs">{hijriDate} هـ</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Users Management ---
function UsersSection() {
  const [users, setUsers] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", name: "", role: "admin" });

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`, { headers: authHeaders() });
      setUsers(data);
    } catch { }
  };

  useEffect(() => { fetchUsers(); }, []);

  const openAdd = () => { setForm({ username: "", password: "", name: "", role: "admin" }); setEditMode(false); setShowDialog(true); };
  const openEdit = (u) => { setForm({ username: u.username, password: "", name: u.name || "", role: u.role || "admin" }); setEditMode(true); setShowDialog(true); };

  const saveUser = async () => {
    if (!editMode && (!form.username || !form.password)) { toast.error("يرجى إدخال اسم المستخدم وكلمة المرور"); return; }
    try {
      if (editMode) {
        await axios.put(`${API}/admin/users/${form.username}`, { name: form.name, role: form.role, password: form.password || undefined }, { headers: authHeaders() });
        toast.success("تم تحديث المستخدم");
      } else {
        await axios.post(`${API}/admin/users`, form, { headers: authHeaders() });
        toast.success("تم إضافة المستخدم");
      }
      setShowDialog(false);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "حدث خطأ"); }
  };

  const deleteUser = async (username) => {
    if (!window.confirm(`هل أنت متأكد من حذف "${username}"؟`)) return;
    try {
      await axios.delete(`${API}/admin/users/${username}`, { headers: authHeaders() });
      toast.success("تم حذف المستخدم");
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "حدث خطأ"); }
  };

  return (
    <div className="bg-[#161618] border border-white/5 rounded-xl p-6" data-testid="users-section">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-white font-bold text-lg flex items-center gap-2"><Users size={18} /> المستخدمين</h3>
        <Button size="sm" className="bg-primary text-white" onClick={openAdd} data-testid="add-user-btn">
          <UserPlus size={14} className="me-1" /> إضافة مستخدم
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="border-white/5">
            <TableHead className="text-zinc-400 text-right">الاسم</TableHead>
            <TableHead className="text-zinc-400 text-right">اسم المستخدم</TableHead>
            <TableHead className="text-zinc-400 text-right">الدور</TableHead>
            <TableHead className="text-zinc-400 text-right">إجراءات</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((u) => (
            <TableRow key={u.username} className="border-white/5">
              <TableCell className="text-white">{u.name || "-"}</TableCell>
              <TableCell className="text-zinc-400 font-mono text-sm">{u.username}</TableCell>
              <TableCell><Badge className="bg-primary/10 text-primary border-0"><Shield size={12} className="me-1" /> مشرف</Badge></TableCell>
              <TableCell>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white" onClick={() => openEdit(u)} data-testid={`edit-user-${u.username}`}><Pencil size={14} /></Button>
                  {u.username !== "admin" && (
                    <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300" onClick={() => deleteUser(u.username)} data-testid={`delete-user-${u.username}`}><Trash2 size={14} /></Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#161618] border-white/10 text-white" dir="rtl">
          <DialogHeader><DialogTitle className="text-white">{editMode ? "تعديل المستخدم" : "إضافة مستخدم جديد"}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">الاسم الكامل</label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-background border-white/10 text-white" placeholder="محمد أحمد" data-testid="user-form-name" />
            </div>
            {!editMode && <div>
              <label className="text-zinc-400 text-sm mb-1 block">اسم المستخدم</label>
              <Input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} className="bg-background border-white/10 text-white" dir="ltr" placeholder="username" data-testid="user-form-username" />
            </div>}
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">{editMode ? "كلمة المرور الجديدة (اتركها فارغة لعدم التغيير)" : "كلمة المرور"}</label>
              <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="bg-background border-white/10 text-white" dir="ltr" placeholder="••••••••" data-testid="user-form-password" />
            </div>
            <Button className="w-full bg-primary text-white" onClick={saveUser} data-testid="user-form-submit">
              <Save size={14} className="me-2" /> {editMode ? "حفظ التعديلات" : "إضافة"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Employees Management ---
const DEPARTMENTS = ["إدارة", "تسويق", "إنتاج", "تدريب", "توظيف", "تقنية", "مالية"];
const STATUSES = ["نشط", "إجازة", "منتهي"];

function EmployeesSection() {
  const settings = useSettings();
  const [employees, setEmployees] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [filter, setFilter] = useState("all");
  const [cardEmp, setCardEmp] = useState(null);
  const cardRef = useRef(null);

  const emptyForm = { name: "", position: "", department: "إدارة", email: "", phone: "", photo_url: "", bio: "", hire_date: "", status: "نشط", employee_id: "", salary: "", emergency_contact: "", emergency_phone: "", notes: "" };

  const fetchEmployees = async () => {
    try {
      const { data } = await axios.get(`${API}/employees`, { headers: authHeaders() });
      setEmployees(data);
    } catch { }
  };

  useEffect(() => { fetchEmployees(); }, []);

  const openAdd = () => { setForm(emptyForm); setEditMode(false); setShowDialog(true); };
  const openEdit = (emp) => { setForm(emp); setEditMode(true); setShowDialog(true); };

  const saveEmployee = async () => {
    if (!form.name) { toast.error("يرجى إدخال اسم الموظف"); return; }
    try {
      if (editMode) {
        await axios.put(`${API}/employees/${form.id}`, form, { headers: authHeaders() });
        toast.success("تم تحديث بيانات الموظف");
      } else {
        await axios.post(`${API}/employees`, form, { headers: authHeaders() });
        toast.success("تم إضافة الموظف");
      }
      setShowDialog(false);
      fetchEmployees();
    } catch (e) { toast.error(e.response?.data?.detail || "حدث خطأ"); }
  };

  const deleteEmployee = async (emp) => {
    if (!window.confirm(`هل أنت متأكد من حذف "${emp.name}"؟`)) return;
    try {
      await axios.delete(`${API}/employees/${emp.id}`, { headers: authHeaders() });
      toast.success("تم حذف الموظف");
      fetchEmployees();
    } catch (e) { toast.error(e.response?.data?.detail || "حدث خطأ"); }
  };

  const downloadCard = () => {
    const el = cardRef.current;
    if (!el) return;
    import("html2canvas").then(({ default: html2canvas }) => {
      html2canvas(el, { scale: 2, useCORS: true, backgroundColor: null }).then(canvas => {
        const link = document.createElement("a");
        link.download = `بطاقة_${cardEmp.name}.png`;
        link.href = canvas.toDataURL("image/png");
        link.click();
        toast.success("تم تحميل البطاقة");
      });
    });
  };

  const printCard = () => {
    const el = cardRef.current;
    if (!el) return;
    import("html2canvas").then(({ default: html2canvas }) => {
      html2canvas(el, { scale: 2, useCORS: true, backgroundColor: null }).then(canvas => {
        const win = window.open("", "_blank");
        win.document.write(`<html><head><title>بطاقة ${cardEmp.name}</title><style>@page{size:auto;margin:10mm}body{display:flex;justify-content:center;align-items:center;min-height:100vh;margin:0;background:#fff}</style></head><body><img src="${canvas.toDataURL("image/png")}" style="max-width:100%;height:auto" /></body></html>`);
        win.document.close();
        win.onload = () => { win.print(); };
      });
    });
  };

  const filtered = filter === "all" ? employees : employees.filter(e => e.department === filter);
  const statusColor = (s) => s === "نشط" ? "bg-green-500/10 text-green-400" : s === "إجازة" ? "bg-yellow-500/10 text-yellow-400" : "bg-red-500/10 text-red-400";

  return (
    <div className="space-y-6" data-testid="employees-section">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[#161618] border border-white/5 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-white">{employees.length}</p>
          <p className="text-zinc-500 text-sm">إجمالي الموظفين</p>
        </div>
        <div className="bg-[#161618] border border-white/5 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-400">{employees.filter(e => e.status === "نشط").length}</p>
          <p className="text-zinc-500 text-sm">نشط</p>
        </div>
        <div className="bg-[#161618] border border-white/5 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-yellow-400">{employees.filter(e => e.status === "إجازة").length}</p>
          <p className="text-zinc-500 text-sm">في إجازة</p>
        </div>
        <div className="bg-[#161618] border border-white/5 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-zinc-400">{new Set(employees.map(e => e.department)).size}</p>
          <p className="text-zinc-500 text-sm">أقسام</p>
        </div>
      </div>

      {/* Main Table */}
      <div className="bg-[#161618] border border-white/5 rounded-xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h3 className="text-white font-bold text-lg flex items-center gap-2"><Briefcase size={18} /> الموظفين</h3>
          <div className="flex gap-2 items-center">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-32 bg-background border-white/10 text-white h-9 text-sm" data-testid="emp-dept-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#161618] border-white/10 text-white">
                <SelectItem value="all">الكل</SelectItem>
                {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" className="bg-primary text-white" onClick={openAdd} data-testid="add-employee-btn">
              <Plus size={14} className="me-1" /> إضافة موظف
            </Button>
          </div>
        </div>

        {filtered.length === 0 ? (
          <p className="text-zinc-500 text-center py-8">لا يوجد موظفين {filter !== "all" ? `في قسم ${filter}` : ""}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/5">
                <TableHead className="text-zinc-400 text-right">الموظف</TableHead>
                <TableHead className="text-zinc-400 text-right">المنصب</TableHead>
                <TableHead className="text-zinc-400 text-right">القسم</TableHead>
                <TableHead className="text-zinc-400 text-right">الحالة</TableHead>
                <TableHead className="text-zinc-400 text-right">التواصل</TableHead>
                <TableHead className="text-zinc-400 text-right">إجراءات</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((emp) => (
                <TableRow key={emp.id} className="border-white/5">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {emp.photo_url ? (
                        <img src={emp.photo_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-bold">{emp.name?.charAt(0)}</div>
                      )}
                      <div>
                        <p className="text-white text-sm font-medium">{emp.name}</p>
                        {emp.employee_id && <p className="text-zinc-600 text-xs">#{emp.employee_id}</p>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-zinc-300 text-sm">{emp.position || "-"}</TableCell>
                  <TableCell><Badge variant="outline" className="border-white/10 text-zinc-300">{emp.department}</Badge></TableCell>
                  <TableCell><Badge className={`border-0 ${statusColor(emp.status)}`}>{emp.status}</Badge></TableCell>
                  <TableCell className="text-zinc-400 text-xs">{emp.phone || emp.email || "-"}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white" onClick={() => openEdit(emp)} data-testid={`edit-emp-${emp.id}`}><Pencil size={14} /></Button>
                      <Button variant="ghost" size="sm" className="text-primary hover:text-primary/80" onClick={() => setCardEmp(emp)} data-testid={`card-emp-${emp.id}`}><CreditCard size={14} /></Button>
                      <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300" onClick={() => deleteEmployee(emp)} data-testid={`delete-emp-${emp.id}`}><Trash2 size={14} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Employee Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#161618] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto" dir="rtl">
          <DialogHeader><DialogTitle className="text-white">{editMode ? "تعديل بيانات الموظف" : "إضافة موظف جديد"}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">الاسم الكامل *</label>
                <Input value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-background border-white/10 text-white" data-testid="emp-form-name" />
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">رقم الموظف</label>
                <Input value={form.employee_id || ""} onChange={e => setForm({ ...form, employee_id: e.target.value })} className="bg-background border-white/10 text-white" dir="ltr" data-testid="emp-form-id" />
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">المنصب</label>
                <Input value={form.position || ""} onChange={e => setForm({ ...form, position: e.target.value })} className="bg-background border-white/10 text-white" data-testid="emp-form-position" />
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">القسم</label>
                <Select value={form.department || "إدارة"} onValueChange={v => setForm({ ...form, department: v })}>
                  <SelectTrigger className="bg-background border-white/10 text-white" data-testid="emp-form-dept"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#161618] border-white/10 text-white">
                    {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">البريد الإلكتروني</label>
                <Input value={form.email || ""} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-background border-white/10 text-white" dir="ltr" data-testid="emp-form-email" />
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">رقم الهاتف</label>
                <Input value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-background border-white/10 text-white" dir="ltr" data-testid="emp-form-phone" />
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">الحالة</label>
                <Select value={form.status || "نشط"} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger className="bg-background border-white/10 text-white" data-testid="emp-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#161618] border-white/10 text-white">
                    {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">تاريخ التعيين</label>
                <Input type="date" value={form.hire_date || ""} onChange={e => setForm({ ...form, hire_date: e.target.value })} className="bg-background border-white/10 text-white" dir="ltr" data-testid="emp-form-hire" />
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">الراتب</label>
                <Input value={form.salary || ""} onChange={e => setForm({ ...form, salary: e.target.value })} className="bg-background border-white/10 text-white" dir="ltr" placeholder="0.00 SAR" data-testid="emp-form-salary" />
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">رابط الصورة</label>
                <Input value={form.photo_url || ""} onChange={e => setForm({ ...form, photo_url: e.target.value })} className="bg-background border-white/10 text-white" dir="ltr" placeholder="https://..." data-testid="emp-form-photo" />
              </div>
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">نبذة</label>
              <Textarea value={form.bio || ""} onChange={e => setForm({ ...form, bio: e.target.value })} className="bg-background border-white/10 text-white" rows={2} data-testid="emp-form-bio" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">جهة اتصال الطوارئ</label>
                <Input value={form.emergency_contact || ""} onChange={e => setForm({ ...form, emergency_contact: e.target.value })} className="bg-background border-white/10 text-white" data-testid="emp-form-emergency" />
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">هاتف الطوارئ</label>
                <Input value={form.emergency_phone || ""} onChange={e => setForm({ ...form, emergency_phone: e.target.value })} className="bg-background border-white/10 text-white" dir="ltr" data-testid="emp-form-emergency-phone" />
              </div>
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">ملاحظات</label>
              <Textarea value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} className="bg-background border-white/10 text-white" rows={2} data-testid="emp-form-notes" />
            </div>
            <Button className="w-full bg-primary text-white" onClick={saveEmployee} data-testid="emp-form-submit">
              <Save size={14} className="me-2" /> {editMode ? "حفظ التعديلات" : "إضافة الموظف"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Employee ID Card Dialog */}
      <Dialog open={!!cardEmp} onOpenChange={(open) => !open && setCardEmp(null)}>
        <DialogContent className="bg-[#161618] border-white/10 text-white max-w-lg" dir="rtl">
          <DialogHeader><DialogTitle className="text-white">بطاقة الموظف</DialogTitle></DialogHeader>
          {cardEmp && (
            <div className="space-y-4 mt-2">
              {/* The Card */}
              <div ref={cardRef} className="mx-auto" style={{ width: 400 }}>
                <div style={{
                  width: 400, borderRadius: 16, overflow: "hidden", fontFamily: "'Zain', sans-serif",
                  background: "linear-gradient(145deg, #0f0f13 0%, #1a1a22 50%, #0f0f13 100%)",
                  border: "1px solid rgba(255,255,255,0.08)", position: "relative"
                }}>
                  {/* Top accent bar */}
                  <div style={{
                    height: 5,
                    background: `linear-gradient(90deg, ${settings.primary_color || "#CB360F"}, ${settings.gradient_color || "#7C3AED"})`
                  }} />

                  {/* Header with logo */}
                  <div style={{ padding: "16px 24px 12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {(settings.logo_dark_url || settings.logo_url) ? (
                        <img src={settings.logo_dark_url || settings.logo_url} alt="" style={{ height: 32, objectFit: "contain" }} crossOrigin="anonymous" />
                      ) : (
                        <span style={{ fontSize: 20, fontWeight: 800, color: settings.primary_color || "#CB360F" }}>{settings.site_name || "رؤية"}</span>
                      )}
                    </div>
                    <span style={{ fontSize: 10, color: "#666", letterSpacing: 1 }}>بطاقة موظف</span>
                  </div>

                  {/* Main content */}
                  <div style={{ padding: "8px 24px 20px", display: "flex", gap: 20, alignItems: "flex-start" }}>
                    {/* Photo */}
                    <div style={{
                      width: 80, height: 80, borderRadius: 12, overflow: "hidden", flexShrink: 0,
                      border: `2px solid ${settings.primary_color || "#CB360F"}30`,
                      background: `${settings.primary_color || "#CB360F"}15`,
                      display: "flex", alignItems: "center", justifyContent: "center"
                    }}>
                      {cardEmp.photo_url ? (
                        <img src={cardEmp.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} crossOrigin="anonymous" />
                      ) : (
                        <span style={{ fontSize: 32, fontWeight: 700, color: settings.primary_color || "#CB360F" }}>{cardEmp.name?.charAt(0)}</span>
                      )}
                    </div>
                    {/* Info */}
                    <div style={{ flex: 1, direction: "rtl" }}>
                      <h3 style={{ fontSize: 20, fontWeight: 800, color: "#fff", margin: "0 0 2px", lineHeight: 1.3 }}>{cardEmp.name}</h3>
                      <p style={{ fontSize: 13, color: settings.primary_color || "#CB360F", fontWeight: 600, margin: "0 0 8px" }}>{cardEmp.position || cardEmp.department}</p>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px" }}>
                        {cardEmp.department && (
                          <span style={{ fontSize: 11, color: "#888" }}>القسم: <span style={{ color: "#ccc" }}>{cardEmp.department}</span></span>
                        )}
                        {cardEmp.employee_id && (
                          <span style={{ fontSize: 11, color: "#888" }}>الرقم: <span style={{ color: "#ccc", direction: "ltr", display: "inline-block" }}>#{cardEmp.employee_id}</span></span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div style={{ margin: "0 24px", height: 1, background: "rgba(255,255,255,0.06)" }} />

                  {/* Contact & details */}
                  <div style={{ padding: "14px 24px 18px", display: "flex", flexWrap: "wrap", gap: "8px 24px", direction: "rtl" }}>
                    {cardEmp.phone && (
                      <span style={{ fontSize: 11, color: "#999", display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ color: "#666" }}>الهاتف:</span> <span dir="ltr" style={{ color: "#ddd" }}>{cardEmp.phone}</span>
                      </span>
                    )}
                    {cardEmp.email && (
                      <span style={{ fontSize: 11, color: "#999", display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ color: "#666" }}>البريد:</span> <span dir="ltr" style={{ color: "#ddd" }}>{cardEmp.email}</span>
                      </span>
                    )}
                    {cardEmp.hire_date && (
                      <span style={{ fontSize: 11, color: "#999", display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ color: "#666" }}>تاريخ التعيين:</span> <span style={{ color: "#ddd" }}>{cardEmp.hire_date}</span>
                      </span>
                    )}
                  </div>

                  {/* Bottom accent */}
                  <div style={{
                    height: 3,
                    background: `linear-gradient(90deg, ${settings.primary_color || "#CB360F"}, ${settings.gradient_color || "#7C3AED"})`,
                    opacity: 0.5
                  }} />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 justify-center">
                <Button className="bg-primary text-white" onClick={downloadCard} data-testid="download-card-btn">
                  <FileText size={14} className="me-2" /> تحميل كصورة
                </Button>
                <Button variant="outline" className="border-white/10 text-white" onClick={printCard} data-testid="print-card-btn">
                  <Eye size={14} className="me-2" /> طباعة
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Stats ---
function StatsOverview({ stats }) {
  const cards = [
    { label: "الأعمال", value: stats.portfolio_count, icon: FolderOpen, color: "text-blue-400" },
    { label: "الدورات", value: stats.courses_count, icon: GraduationCap, color: "text-green-400" },
    { label: "الوظائف", value: stats.jobs_count, icon: Briefcase, color: "text-cyan-400" },
    { label: "آراء العملاء", value: stats.testimonials_count, icon: MessageSquare, color: "text-yellow-400" },
    { label: "الشركاء", value: stats.partners_count, icon: Handshake, color: "text-purple-400" },
    { label: "الرسائل", value: stats.messages_count, icon: Mail, color: "text-primary" },
    { label: "غير مقروءة", value: stats.unread_messages, icon: MailOpen, color: "text-red-400" },
    { label: "طلبات التوظيف", value: stats.applications_count, icon: FileText, color: "text-teal-400" },
    { label: "الموظفين", value: stats.employees_count, icon: Users, color: "text-indigo-400" },
    { label: "صناع المحتوى", value: stats.influencers_count || 0, icon: Star, color: "text-orange-400" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
      {cards.map((c, i) => {
        const Icon = c.icon;
        return (
          <div key={i} className="bg-[#161618] border border-white/5 rounded-xl p-4" data-testid={`stat-card-${i}`}>
            <Icon size={20} className={`${c.color} mb-2`} />
            <div className="text-2xl font-bold text-white font-['Zain']">{c.value}</div>
            <div className="text-zinc-500 text-xs">{c.label}</div>
          </div>
        );
      })}
    </div>
  );
}

// --- Generic CRUD ---
function CrudSection({ title, items, columns, onAdd, onEdit, onDelete, renderForm }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const openAdd = () => { setEditItem(null); setDialogOpen(true); };
  const openEdit = (item) => { setEditItem(item); setDialogOpen(true); };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold font-['Zain'] text-white">{title}</h2>
        <Button onClick={openAdd} className="bg-primary hover:bg-[#E54D2E] text-white" data-testid={`add-${title}-btn`}>
          <Plus size={16} className="me-2" /> إضافة
        </Button>
      </div>
      <div className="bg-[#161618] border border-white/5 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              {columns.map((col, i) => <TableHead key={i} className="text-zinc-400 text-right">{col.label}</TableHead>)}
              <TableHead className="text-zinc-400 text-right">إجراءات</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id} className="border-white/5 hover:bg-white/5">
                {columns.map((col, i) => (
                  <TableCell key={i} className="text-zinc-300 text-sm">
                    {col.render ? col.render(item) : item[col.key]}
                  </TableCell>
                ))}
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400 hover:text-white" onClick={() => openEdit(item)}><Pencil size={14} /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400 hover:text-red-400" onClick={() => onDelete(item.id)}><Trash2 size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={columns.length + 1} className="text-center text-zinc-500 py-8">لا توجد بيانات</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#161618] border-white/10 text-white max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Zain'] text-right">{editItem ? "تعديل" : "إضافة جديد"}</DialogTitle></DialogHeader>
          {renderForm(editItem, (data) => { if (editItem) onEdit(editItem.id, data); else onAdd(data); setDialogOpen(false); })}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Portfolio Form ---
function PortfolioForm({ item, onSubmit }) {
  const [form, setForm] = useState(item || { title: "", description: "", category: "marketing", image_url: "", video_url: "", client_name: "" });
  return (
    <div className="space-y-4" data-testid="portfolio-form">
      <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="العنوان" className="bg-background border-white/10 text-white text-right" />
      <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="الوصف" className="bg-background border-white/10 text-white text-right" />
      <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
        <SelectTrigger className="bg-background border-white/10 text-white"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-[#1c1c1f] border-white/10">
          <SelectItem value="marketing">تسويق</SelectItem>
          <SelectItem value="production">إنتاج</SelectItem>
          <SelectItem value="training">تدريب</SelectItem>
          <SelectItem value="ads">إعلانات</SelectItem>
        </SelectContent>
      </Select>
      <Input value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} placeholder="رابط الصورة" className="bg-background border-white/10 text-white" dir="ltr" />
      <Input value={form.video_url || ""} onChange={e => setForm({ ...form, video_url: e.target.value })} placeholder="رابط فيديو يوتيوب (اختياري)" className="bg-background border-white/10 text-white" dir="ltr" data-testid="portfolio-video-url" />
      <Input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} placeholder="اسم العميل" className="bg-background border-white/10 text-white text-right" />
      <Button onClick={() => onSubmit(form)} className="bg-primary hover:bg-[#E54D2E] text-white w-full">حفظ</Button>
    </div>
  );
}

// --- Course Form ---
function CourseForm({ item, onSubmit }) {
  const [form, setForm] = useState(item || { title: "", description: "", duration: "", price: "", category: "course", image_url: "", features: [], registration_open: true });
  const [featText, setFeatText] = useState("");
  const addFeature = () => { if (featText) { setForm({ ...form, features: [...form.features, featText] }); setFeatText(""); } };
  return (
    <div className="space-y-4" data-testid="course-form">
      <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="عنوان الدورة" className="bg-background border-white/10 text-white text-right" />
      <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="الوصف" className="bg-background border-white/10 text-white text-right" />
      <div className="grid grid-cols-2 gap-3">
        <Input value={form.duration} onChange={e => setForm({ ...form, duration: e.target.value })} placeholder="المدة" className="bg-background border-white/10 text-white text-right" />
        <Input value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} placeholder="السعر" className="bg-background border-white/10 text-white text-right" />
      </div>
      <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
        <SelectTrigger className="bg-background border-white/10 text-white"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-[#1c1c1f] border-white/10"><SelectItem value="course">دورة</SelectItem><SelectItem value="workshop">ورشة عمل</SelectItem><SelectItem value="program">برنامج</SelectItem></SelectContent>
      </Select>
      <Input value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} placeholder="رابط الصورة" className="bg-background border-white/10 text-white" dir="ltr" />
      <div>
        <div className="flex gap-2 mb-2">
          <Input value={featText} onChange={e => setFeatText(e.target.value)} placeholder="أضف ميزة" className="bg-background border-white/10 text-white text-right flex-1" />
          <Button type="button" onClick={addFeature} variant="outline" className="border-white/10 text-white">+</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {form.features.map((f, i) => (
            <Badge key={i} className="bg-primary/10 text-primary cursor-pointer" onClick={() => setForm({ ...form, features: form.features.filter((_, idx) => idx !== i) })}>{f} <X size={12} className="ms-1" /></Badge>
          ))}
        </div>
      </div>
      <Button onClick={() => onSubmit(form)} className="bg-primary hover:bg-[#E54D2E] text-white w-full">حفظ</Button>
    </div>
  );
}

// --- Courses Admin Section ---
function CoursesAdminSection({ courses, onRefresh }) {
  const [regCounts, setRegCounts] = useState({});
  const [viewRegs, setViewRegs] = useState(null);
  const [regs, setRegs] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);

  useEffect(() => {
    courses.forEach(c => {
      axios.get(`${API}/courses/${c.id}/registrations/count`, { headers: authHeaders() }).then(r => {
        setRegCounts(prev => ({ ...prev, [c.id]: r.data.count }));
      }).catch(() => {});
    });
  }, [courses]);

  const loadRegs = async (course) => {
    setViewRegs(course);
    const { data } = await axios.get(`${API}/courses/${course.id}/registrations`, { headers: authHeaders() });
    setRegs(data);
  };

  const toggleReg = async (course) => {
    await axios.put(`${API}/courses/${course.id}/toggle-registration`, {}, { headers: authHeaders() });
    toast.success(course.registration_open !== false ? "تم إغلاق التسجيل" : "تم فتح التسجيل");
    onRefresh();
  };

  const exportCSV = (course) => {
    const token = localStorage.getItem("admin_token");
    window.open(`${API}/courses/${course.id}/registrations/export?token=${token}`, "_blank");
  };

  const deleteCourse = async (id) => {
    if (!window.confirm("هل أنت متأكد من الحذف؟")) return;
    await axios.delete(`${API}/courses/${id}`, { headers: authHeaders() });
    toast.success("تم الحذف");
    onRefresh();
  };

  const saveCourse = async (data) => {
    if (editItem) {
      await axios.put(`${API}/courses/${editItem.id}`, data, { headers: authHeaders() });
      toast.success("تم التحديث");
    } else {
      await axios.post(`${API}/courses`, data, { headers: authHeaders() });
      toast.success("تمت الإضافة");
    }
    setShowAdd(false);
    setEditItem(null);
    onRefresh();
  };

  const deleteReg = async (regId) => {
    await axios.delete(`${API}/courses/${viewRegs.id}/registrations/${regId}`, { headers: authHeaders() });
    toast.success("تم الحذف");
    loadRegs(viewRegs);
  };

  return (
    <div className="space-y-6" data-testid="courses-admin">
      <div className="flex justify-between items-center">
        <div />
        <Button className="bg-primary text-white" onClick={() => { setEditItem(null); setShowAdd(true); }} data-testid="add-course-btn">
          <Plus size={14} className="me-2" /> إضافة دورة
        </Button>
      </div>

      <div className="grid gap-4">
        {courses.map(course => (
          <div key={course.id} className="bg-[#161618] border border-white/5 rounded-xl p-5 flex flex-col md:flex-row gap-4 items-start" data-testid={`admin-course-${course.id}`}>
            {course.image_url && (
              <img src={course.image_url} alt="" className="w-20 h-14 rounded-lg object-cover flex-shrink-0 border border-white/5" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-white font-bold text-sm">{course.title}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full ${course.registration_open !== false ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
                  {course.registration_open !== false ? "التسجيل مفتوح" : "التسجيل مغلق"}
                </span>
              </div>
              <p className="text-zinc-500 text-xs">{course.duration} • {course.price} • {course.category === "course" ? "دورة" : course.category === "workshop" ? "ورشة عمل" : "برنامج"}</p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                onClick={() => loadRegs(course)}
                className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors"
                data-testid={`view-regs-${course.id}`}
              >
                <Users size={14} />
                <span className="font-bold">{regCounts[course.id] || 0}</span>
                <span className="text-zinc-500 text-xs">مسجّل</span>
              </button>
              <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white" onClick={() => { setEditItem(course); setShowAdd(true); }}><Pencil size={14} /></Button>
              <Button variant="ghost" size="sm" className={course.registration_open !== false ? "text-yellow-400 hover:text-yellow-300" : "text-green-400 hover:text-green-300"} onClick={() => toggleReg(course)} data-testid={`toggle-reg-${course.id}`}>
                {course.registration_open !== false ? <X size={14} /> : <Check size={14} />}
              </Button>
              <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300" onClick={() => deleteCourse(course.id)}><Trash2 size={14} /></Button>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-[#161618] border-white/10 text-white max-w-md" dir="rtl">
          <DialogHeader><DialogTitle className="text-white">{editItem ? "تعديل الدورة" : "إضافة دورة"}</DialogTitle></DialogHeader>
          <CourseForm item={editItem} onSubmit={saveCourse} />
        </DialogContent>
      </Dialog>

      {/* Registrations Dialog */}
      <Dialog open={!!viewRegs} onOpenChange={(open) => !open && setViewRegs(null)}>
        <DialogContent className="bg-[#161618] border-white/10 text-white max-w-2xl" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-white">المسجلين في: {viewRegs?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="flex justify-between items-center">
              <span className="text-zinc-400 text-sm">عدد المسجلين: <span className="text-white font-bold">{regs.length}</span></span>
              {regs.length > 0 && (
                <Button variant="outline" size="sm" className="border-white/10 text-white" onClick={() => exportCSV(viewRegs)} data-testid="export-regs-btn">
                  <FileText size={14} className="me-2" /> تصدير Excel
                </Button>
              )}
            </div>
            {regs.length === 0 ? (
              <p className="text-center text-zinc-600 py-8">لا يوجد مسجلين حتى الآن</p>
            ) : (
              <div className="max-h-80 overflow-y-auto space-y-2">
                {regs.map(r => (
                  <div key={r.id} className="bg-white/[0.03] border border-white/5 rounded-lg p-3 flex justify-between items-start">
                    <div>
                      <p className="text-white text-sm font-bold">{r.name}</p>
                      <p className="text-zinc-500 text-xs mt-0.5" dir="ltr">{r.phone} {r.email && `• ${r.email}`}</p>
                      {r.notes && <p className="text-zinc-600 text-xs mt-1">{r.notes}</p>}
                      <p className="text-zinc-700 text-[10px] mt-1">{new Date(r.created_at).toLocaleDateString("ar-SA")}</p>
                    </div>
                    <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 flex-shrink-0" onClick={() => deleteReg(r.id)}><Trash2 size={12} /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Job Form ---
function JobForm({ item, onSubmit }) {
  const [form, setForm] = useState(item || { title: "", department: "", location: "", job_type: "full-time", description: "", requirements: [], is_active: true });
  const [reqText, setReqText] = useState("");
  const addReq = () => { if (reqText) { setForm({ ...form, requirements: [...form.requirements, reqText] }); setReqText(""); } };
  return (
    <div className="space-y-4" data-testid="job-form">
      <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="المسمى الوظيفي" className="bg-background border-white/10 text-white text-right" />
      <div className="grid grid-cols-2 gap-3">
        <Input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} placeholder="القسم" className="bg-background border-white/10 text-white text-right" />
        <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="الموقع" className="bg-background border-white/10 text-white text-right" />
      </div>
      <Select value={form.job_type} onValueChange={v => setForm({ ...form, job_type: v })}>
        <SelectTrigger className="bg-background border-white/10 text-white"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-[#1c1c1f] border-white/10">
          <SelectItem value="full-time">دوام كامل</SelectItem>
          <SelectItem value="part-time">دوام جزئي</SelectItem>
          <SelectItem value="remote">عن بعد</SelectItem>
          <SelectItem value="contract">عقد مؤقت</SelectItem>
        </SelectContent>
      </Select>
      <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="وصف الوظيفة" className="bg-background border-white/10 text-white text-right" />
      <div>
        <div className="flex gap-2 mb-2">
          <Input value={reqText} onChange={e => setReqText(e.target.value)} placeholder="أضف متطلب" className="bg-background border-white/10 text-white text-right flex-1" />
          <Button type="button" onClick={addReq} variant="outline" className="border-white/10 text-white">+</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {form.requirements.map((r, i) => (
            <Badge key={i} className="bg-primary/10 text-primary cursor-pointer" onClick={() => setForm({ ...form, requirements: form.requirements.filter((_, idx) => idx !== i) })}>{r} <X size={12} className="ms-1" /></Badge>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} />
        <span className="text-zinc-400 text-sm">{form.is_active ? "نشطة" : "معطّلة"}</span>
      </div>
      <Button onClick={() => onSubmit(form)} className="bg-primary hover:bg-[#E54D2E] text-white w-full">حفظ</Button>
    </div>
  );
}

// --- Testimonial Form ---
function TestimonialForm({ item, onSubmit }) {
  const [form, setForm] = useState(item || { name: "", role: "", company: "", content: "", rating: 5, avatar_url: "" });
  return (
    <div className="space-y-4" data-testid="testimonial-form">
      <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="الاسم" className="bg-background border-white/10 text-white text-right" />
      <div className="grid grid-cols-2 gap-3">
        <Input value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} placeholder="المنصب" className="bg-background border-white/10 text-white text-right" />
        <Input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder="الشركة" className="bg-background border-white/10 text-white text-right" />
      </div>
      <Textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} placeholder="نص الشهادة" className="bg-background border-white/10 text-white text-right" />
      <Input type="number" min={1} max={5} value={form.rating} onChange={e => setForm({ ...form, rating: parseInt(e.target.value) })} placeholder="التقييم (1-5)" className="bg-background border-white/10 text-white" />
      <Button onClick={() => onSubmit(form)} className="bg-primary hover:bg-[#E54D2E] text-white w-full">حفظ</Button>
    </div>
  );
}

// --- Partner Form ---
function PartnerForm({ item, onSubmit }) {
  const [form, setForm] = useState(item || { name: "", logo_url: "", website: "" });
  return (
    <div className="space-y-4" data-testid="partner-form">
      <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="اسم الشريك" className="bg-background border-white/10 text-white text-right" />
      <Input value={form.logo_url} onChange={e => setForm({ ...form, logo_url: e.target.value })} placeholder="رابط الشعار" className="bg-background border-white/10 text-white" dir="ltr" />
      <Input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} placeholder="الموقع الإلكتروني" className="bg-background border-white/10 text-white" dir="ltr" />
      <Button onClick={() => onSubmit(form)} className="bg-primary hover:bg-[#E54D2E] text-white w-full">حفظ</Button>
    </div>
  );
}

function InfluencerForm({ item, onSubmit }) {
  const [form, setForm] = useState(item || { name: "", image_url: "", bio: "", category: "محتوى عام", followers_count: "", instagram: "", tiktok: "", youtube: "", snapchat: "", twitter: "", is_featured: true, is_active: true });
  return (
    <div className="space-y-4" data-testid="influencer-form">
      <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="اسم المؤثر *" className="bg-background border-white/10 text-white text-right" data-testid="influencer-name" />
      <Input value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} placeholder="رابط الصورة" className="bg-background border-white/10 text-white" dir="ltr" data-testid="influencer-image" />
      <Textarea value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} placeholder="نبذة مختصرة" className="bg-background border-white/10 text-white text-right" data-testid="influencer-bio" />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-zinc-500 text-xs mb-1 block">التصنيف</label>
          <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
            <SelectTrigger className="bg-background border-white/10 text-white" data-testid="influencer-category"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1c1c1f] border-white/10">
              <SelectItem value="محتوى عام" className="text-white">محتوى عام</SelectItem>
              <SelectItem value="تسويق" className="text-white">تسويق</SelectItem>
              <SelectItem value="إعلانات" className="text-white">إعلانات</SelectItem>
              <SelectItem value="ترفيه" className="text-white">ترفيه</SelectItem>
              <SelectItem value="تقنية" className="text-white">تقنية</SelectItem>
              <SelectItem value="طبخ" className="text-white">طبخ</SelectItem>
              <SelectItem value="أزياء" className="text-white">أزياء</SelectItem>
              <SelectItem value="رياضة" className="text-white">رياضة</SelectItem>
              <SelectItem value="تعليم" className="text-white">تعليم</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Input value={form.followers_count} onChange={e => setForm({ ...form, followers_count: e.target.value })} placeholder="عدد المتابعين (مثال: 500K)" className="bg-background border-white/10 text-white mt-5" data-testid="influencer-followers" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Input value={form.instagram} onChange={e => setForm({ ...form, instagram: e.target.value })} placeholder="رابط انستقرام" className="bg-background border-white/10 text-white" dir="ltr" />
        <Input value={form.tiktok} onChange={e => setForm({ ...form, tiktok: e.target.value })} placeholder="رابط تيك توك" className="bg-background border-white/10 text-white" dir="ltr" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Input value={form.youtube} onChange={e => setForm({ ...form, youtube: e.target.value })} placeholder="رابط يوتيوب" className="bg-background border-white/10 text-white" dir="ltr" />
        <Input value={form.snapchat} onChange={e => setForm({ ...form, snapchat: e.target.value })} placeholder="رابط سناب شات" className="bg-background border-white/10 text-white" dir="ltr" />
      </div>
      <Input value={form.twitter} onChange={e => setForm({ ...form, twitter: e.target.value })} placeholder="رابط تويتر / X" className="bg-background border-white/10 text-white" dir="ltr" />
      <div className="flex items-center gap-6 pt-2">
        <label className="flex items-center gap-2 text-zinc-300 text-sm cursor-pointer">
          <Switch checked={form.is_featured} onCheckedChange={v => setForm({ ...form, is_featured: v })} data-testid="influencer-featured" />
          عرض في الرئيسية
        </label>
        <label className="flex items-center gap-2 text-zinc-300 text-sm cursor-pointer">
          <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} data-testid="influencer-active" />
          مفعّل
        </label>
      </div>
      <Button onClick={() => onSubmit(form)} className="bg-primary hover:bg-[#E54D2E] text-white w-full" data-testid="influencer-submit">حفظ</Button>
    </div>
  );
}

// --- Messages ---
function MessagesSection({ messages, onMarkRead, onDelete }) {
  return (
    <div>
      <h2 className="text-xl font-bold font-['Zain'] text-white mb-4">الرسائل</h2>
      <div className="space-y-3">
        {messages.map((msg) => (
          <div key={msg.id} className={`bg-[#161618] border rounded-xl p-5 ${msg.is_read ? 'border-white/5' : 'border-primary/30'}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-white font-semibold text-sm">{msg.name}</span>
                  {!msg.is_read && <Badge className="bg-primary/20 text-primary text-xs">جديد</Badge>}
                </div>
                <p className="text-zinc-400 text-xs mb-1">{msg.email} {msg.phone && `| ${msg.phone}`}</p>
                <p className="text-zinc-300 text-sm font-medium mb-1">{msg.subject}</p>
                <p className="text-zinc-400 text-sm">{msg.message}</p>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                {!msg.is_read && <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400 hover:text-green-400" onClick={() => onMarkRead(msg.id)}><Eye size={14} /></Button>}
                <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400 hover:text-red-400" onClick={() => onDelete(msg.id)}><Trash2 size={14} /></Button>
              </div>
            </div>
          </div>
        ))}
        {messages.length === 0 && <div className="text-center py-12 text-zinc-500">لا توجد رسائل</div>}
      </div>
    </div>
  );
}

// --- Applications ---
function ApplicationsSection({ applications, jobs, onDelete }) {
  const [filterJob, setFilterJob] = useState("all");
  const statusLabels = { pending: "قيد المراجعة", reviewed: "تمت المراجعة", accepted: "مقبول", rejected: "مرفوض" };
  const statusColors = { pending: "bg-yellow-500/10 text-yellow-400", reviewed: "bg-blue-500/10 text-blue-400", accepted: "bg-green-500/10 text-green-400", rejected: "bg-red-500/10 text-red-400" };

  const exportExcel = () => {
    const token = localStorage.getItem("admin_token");
    window.open(`${API}/jobs/applications/export?token=${token}`, "_blank");
  };

  const updateStatus = async (appId, status) => {
    try {
      await axios.put(`${API}/jobs/applications/${appId}/status`, { status }, { headers: authHeaders() });
      toast.success("تم تحديث الحالة");
      window.location.reload();
    } catch (e) { toast.error("حدث خطأ"); }
  };

  const openWhatsApp = (phone, name, jobTitle) => {
    let cleaned = phone.replace(/[\s\-()]/g, "");
    if (cleaned.startsWith("0")) cleaned = "966" + cleaned.slice(1);
    if (!cleaned.startsWith("+") && !cleaned.startsWith("966")) cleaned = "966" + cleaned;
    cleaned = cleaned.replace("+", "");
    const msg = encodeURIComponent(`مرحباً ${name}، بخصوص تقديمك على وظيفة "${jobTitle}" في رؤية.`);
    window.open(`https://wa.me/${cleaned}?text=${msg}`, "_blank");
  };

  const filtered = filterJob === "all" ? applications : applications.filter(a => a.job_id === filterJob);
  const uniqueJobs = jobs || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-xl font-bold font-['Zain'] text-white">طلبات التوظيف ({filtered.length})</h2>
          {uniqueJobs.length > 0 && (
            <Select value={filterJob} onValueChange={setFilterJob}>
              <SelectTrigger className="h-8 w-auto min-w-[160px] bg-transparent border-white/10 text-zinc-300 text-xs" data-testid="filter-applications-job">
                <Filter size={12} className="me-1.5 text-zinc-500" />
                <SelectValue placeholder="فلتر حسب الوظيفة" />
              </SelectTrigger>
              <SelectContent className="bg-[#1c1c1f] border-white/10">
                <SelectItem value="all" className="text-zinc-300 text-xs">كل الوظائف</SelectItem>
                {uniqueJobs.map(j => (
                  <SelectItem key={j.id} value={j.id} className="text-zinc-300 text-xs">{j.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        {filtered.length > 0 && (
          <Button variant="outline" size="sm" className="border-white/10 text-white hover:bg-white/5" onClick={exportExcel} data-testid="export-applications-btn">
            <FileText size={14} className="me-2" /> تصدير Excel
          </Button>
        )}
      </div>
      <div className="space-y-3">
        {filtered.map((app) => (
          <div key={app.id} className="bg-[#161618] border border-white/5 rounded-xl p-5" data-testid={`application-${app.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="text-white font-semibold text-sm">{app.name}</span>
                  <Badge className={`${statusColors[app.status]} text-xs border-0`}>{statusLabels[app.status]}</Badge>
                </div>
                <p className="text-primary text-xs font-medium mb-1">{app.job_title}</p>
                <p className="text-zinc-400 text-xs">{app.email} | {app.phone}</p>
                {app.cover_letter && <p className="text-zinc-500 text-sm mt-2 leading-relaxed">{app.cover_letter}</p>}
                <div className="flex items-center gap-3 mt-3 flex-wrap">
                  {app.cv_url && <a href={app.cv_url.startsWith("http") ? app.cv_url : `${process.env.REACT_APP_BACKEND_URL}${app.cv_url}`} target="_blank" rel="noopener noreferrer" className="text-primary text-xs hover:underline flex items-center gap-1" data-testid={`cv-link-${app.id}`}><FileText size={12} /> عرض السيرة الذاتية</a>}
                  <button onClick={() => openWhatsApp(app.phone, app.name, app.job_title)} className="flex items-center gap-1 text-green-400 text-xs hover:text-green-300 transition-colors cursor-pointer" data-testid={`whatsapp-${app.id}`}>
                    <MessageCircle size={12} /> واتساب
                  </button>
                  <Select defaultValue={app.status} onValueChange={(v) => updateStatus(app.id, v)}>
                    <SelectTrigger className="h-7 w-auto min-w-[120px] bg-transparent border-white/10 text-zinc-400 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1c1c1f] border-white/10">
                      <SelectItem value="pending" className="text-yellow-400 text-xs">قيد المراجعة</SelectItem>
                      <SelectItem value="reviewed" className="text-blue-400 text-xs">تمت المراجعة</SelectItem>
                      <SelectItem value="accepted" className="text-green-400 text-xs">مقبول</SelectItem>
                      <SelectItem value="rejected" className="text-red-400 text-xs">مرفوض</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-zinc-700 text-[10px]">{app.created_at ? new Date(app.created_at).toLocaleDateString("ar-SA") : ""}</span>
                </div>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400 hover:text-red-400 flex-shrink-0" onClick={() => onDelete(app.id)}><Trash2 size={14} /></Button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="text-center py-12 text-zinc-500">{filterJob !== "all" ? "لا توجد طلبات لهذه الوظيفة" : "لا توجد طلبات"}</div>}
      </div>
    </div>
  );
}

// --- Settings ---
function SettingsSection({ settings, onSave }) {
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(settings); }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div data-testid="settings-section">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold font-['Zain'] text-white">إعدادات المنصة</h2>
        <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-[#E54D2E] text-white" data-testid="save-settings-btn">
          <Save size={16} className="me-2" />
          {saving ? "جاري الحفظ..." : "حفظ الإعدادات"}
        </Button>
      </div>

      <Tabs defaultValue="general" dir="rtl">
        <TabsList className="bg-[#161618] border border-white/5 mb-6">
          <TabsTrigger value="general" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary">عام</TabsTrigger>
          <TabsTrigger value="contact" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary">التواصل</TabsTrigger>
          <TabsTrigger value="social" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary">التواصل الاجتماعي</TabsTrigger>
          <TabsTrigger value="seo" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary">SEO</TabsTrigger>
          <TabsTrigger value="images" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary">الصور</TabsTrigger>
          <TabsTrigger value="tr_page" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary">التدريب والتوظيف</TabsTrigger>
          <TabsTrigger value="advanced" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary">متقدم</TabsTrigger>
        </TabsList>

        {/* General */}
        <TabsContent value="general">
          <div className="bg-[#161618] border border-white/5 rounded-xl p-6 space-y-5">
            <div>
              <label className="text-zinc-400 text-sm mb-1.5 flex items-center gap-2"><Globe size={14} /> اسم الموقع</label>
              <Input value={form.site_name} onChange={e => setForm({ ...form, site_name: e.target.value })} className="bg-background border-white/10 text-white text-right" data-testid="setting-site-name" />
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1.5 block">وصف الموقع</label>
              <Textarea value={form.site_description} onChange={e => setForm({ ...form, site_description: e.target.value })} className="bg-background border-white/10 text-white text-right" data-testid="setting-site-desc" />
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1.5 block">رابط الشعار (عام)</label>
              <Input value={form.logo_url} onChange={e => setForm({ ...form, logo_url: e.target.value })} className="bg-background border-white/10 text-white" dir="ltr" placeholder="https://..." data-testid="setting-logo" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-zinc-400 text-sm mb-1.5 block">شعار الوضع الليلي</label>
                <Input value={form.logo_dark_url || ""} onChange={e => setForm({ ...form, logo_dark_url: e.target.value })} className="bg-background border-white/10 text-white" dir="ltr" placeholder="https://..." data-testid="setting-logo-dark" />
                {form.logo_dark_url && <img src={form.logo_dark_url} alt="Dark Logo" className="h-8 mt-2 object-contain rounded bg-[#111112] p-1" />}
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-1.5 block">شعار الوضع النهاري</label>
                <Input value={form.logo_light_url || ""} onChange={e => setForm({ ...form, logo_light_url: e.target.value })} className="bg-background border-white/10 text-white" dir="ltr" placeholder="https://..." data-testid="setting-logo-light" />
                {form.logo_light_url && <img src={form.logo_light_url} alt="Light Logo" className="h-8 mt-2 object-contain rounded bg-white p-1" />}
              </div>
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1.5 block">نص الفوتر</label>
              <Input value={form.footer_text} onChange={e => setForm({ ...form, footer_text: e.target.value })} className="bg-background border-white/10 text-white text-right" data-testid="setting-footer" />
            </div>
          </div>
        </TabsContent>

        {/* Contact */}
        <TabsContent value="contact">
          <div className="bg-[#161618] border border-white/5 rounded-xl p-6 space-y-5">
            <div>
              <label className="text-zinc-400 text-sm mb-1.5 flex items-center gap-2"><Phone size={14} /> رقم الهاتف</label>
              <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-background border-white/10 text-white" dir="ltr" data-testid="setting-phone" />
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1.5 flex items-center gap-2"><AtSign size={14} /> البريد الإلكتروني</label>
              <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-background border-white/10 text-white" dir="ltr" data-testid="setting-email" />
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1.5 block">رقم الواتساب (بدون +)</label>
              <Input value={form.whatsapp} onChange={e => setForm({ ...form, whatsapp: e.target.value })} className="bg-background border-white/10 text-white" dir="ltr" data-testid="setting-whatsapp" />
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1.5 flex items-center gap-2"><MapPin size={14} /> العنوان</label>
              <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="bg-background border-white/10 text-white text-right" data-testid="setting-address" />
            </div>
          </div>
        </TabsContent>

        {/* Social */}
        <TabsContent value="social">
          <div className="bg-[#161618] border border-white/5 rounded-xl p-6 space-y-5">
            {[
              { key: "instagram", label: "انستقرام", icon: Instagram },
              { key: "twitter", label: "تويتر (X)", icon: Globe },
              { key: "linkedin", label: "لينكدإن", icon: Globe },
              { key: "youtube", label: "يوتيوب", icon: Youtube },
              { key: "tiktok", label: "تيك توك", icon: Globe },
              { key: "snapchat", label: "سناب شات", icon: Globe },
            ].map(({ key, label, icon: Icon }) => (
              <div key={key}>
                <label className="text-zinc-400 text-sm mb-1.5 flex items-center gap-2"><Icon size={14} /> {label}</label>
                <Input value={form[key] || ""} onChange={e => setForm({ ...form, [key]: e.target.value })} className="bg-background border-white/10 text-white" dir="ltr" placeholder="https://..." data-testid={`setting-${key}`} />
              </div>
            ))}
          </div>
        </TabsContent>

        {/* SEO */}
        <TabsContent value="seo">
          <div className="bg-[#161618] border border-white/5 rounded-xl p-6 space-y-5">
            <div>
              <label className="text-zinc-400 text-sm mb-1.5 block">عنوان الصفحة (Meta Title)</label>
              <Input value={form.meta_title} onChange={e => setForm({ ...form, meta_title: e.target.value })} className="bg-background border-white/10 text-white text-right" data-testid="setting-meta-title" />
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1.5 block">وصف الصفحة (Meta Description)</label>
              <Textarea value={form.meta_description} onChange={e => setForm({ ...form, meta_description: e.target.value })} className="bg-background border-white/10 text-white text-right" data-testid="setting-meta-desc" />
            </div>
          </div>
        </TabsContent>

        {/* Images */}
        <TabsContent value="images">
          <div className="bg-[#161618] border border-white/5 rounded-xl p-6 space-y-6">
            <p className="text-zinc-400 text-sm">تحكم بصور الصفحات الرئيسية. ضع رابط الصورة أو ارفعها من قسم الملفات. اتركها فارغة لاستخدام الصورة الافتراضية.</p>

            {[
              { key: "hero_image", label: "صورة البانر الرئيسي", desc: "الصورة الكبيرة في أعلى الصفحة الرئيسية" },
              { key: "marketing_image", label: "صورة صفحة التسويق", desc: "خلفية بانر صفحة التسويق" },
              { key: "production_image", label: "صورة صفحة الإنتاج", desc: "خلفية بانر صفحة الإنتاج" },
              { key: "training_image", label: "صورة صفحة التدريب", desc: "خلفية بانر صفحة التدريب" },
              { key: "about_image", label: "صورة صفحة من نحن", desc: "خلفية بانر صفحة من نحن" },
              { key: "about_team_image", label: "صورة الفريق", desc: "صورة الفريق في صفحة من نحن" },
              { key: "recruitment_image", label: "صورة صفحة التوظيف", desc: "الصورة الجانبية في صفحة التوظيف" },
            ].map(({ key, label, desc }) => (
              <div key={key} className="space-y-2">
                <label className="text-zinc-300 text-sm font-medium block">{label}</label>
                <p className="text-zinc-600 text-xs">{desc}</p>
                <Input
                  value={form[key] || ""}
                  onChange={e => setForm({ ...form, [key]: e.target.value })}
                  className="bg-background border-white/10 text-white"
                  dir="ltr"
                  placeholder="https://..."
                  data-testid={`setting-${key}`}
                />
                {form[key] && (
                  <img src={form[key]} alt={label} className="h-24 mt-1 rounded-lg object-cover border border-white/5" />
                )}
              </div>
            ))}

            {/* Map Embed */}
            <div className="space-y-2 pt-4 border-t border-white/5">
              <label className="text-zinc-300 text-sm font-medium block">رابط خريطة جوجل (Embed)</label>
              <p className="text-zinc-600 text-xs">افتح Google Maps &gt; اضغط مشاركة &gt; تضمين خريطة &gt; انسخ رابط src من الكود. تظهر في الفوتر تحت العنوان.</p>
              <Input
                value={form.map_embed_url || ""}
                onChange={e => setForm({ ...form, map_embed_url: e.target.value })}
                className="bg-background border-white/10 text-white"
                dir="ltr"
                placeholder="https://www.google.com/maps/embed?pb=..."
                data-testid="setting-map_embed_url"
              />
              {form.map_embed_url && (
                <iframe
                  src={form.map_embed_url}
                  className="w-full h-32 rounded-lg border border-white/5 mt-1"
                  style={{ filter: "invert(90%) hue-rotate(180deg)" }}
                  loading="lazy"
                  title="map-preview"
                />
              )}
            </div>
          </div>
        </TabsContent>

        {/* Training & Recruitment Page Content */}
        <TabsContent value="tr_page">
          <div className="space-y-6">
            {/* Hero Section */}
            <div className="bg-[#161618] border border-white/5 rounded-xl p-6 space-y-4">
              <h3 className="text-white font-bold font-['Zain'] text-sm border-b border-white/5 pb-2">البانر الرئيسي</h3>
              <div>
                <label className="text-zinc-400 text-xs mb-1.5 block">الشارة العلوية</label>
                <Input value={form.tr_hero_badge || ""} onChange={e => setForm({ ...form, tr_hero_badge: e.target.value })} className="bg-background border-white/10 text-white text-right" data-testid="setting-tr-hero-badge" />
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1.5 block">العنوان الرئيسي</label>
                <Input value={form.tr_hero_title || ""} onChange={e => setForm({ ...form, tr_hero_title: e.target.value })} className="bg-background border-white/10 text-white text-right" data-testid="setting-tr-hero-title" />
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1.5 block">الوصف</label>
                <Textarea value={form.tr_hero_subtitle || ""} onChange={e => setForm({ ...form, tr_hero_subtitle: e.target.value })} className="bg-background border-white/10 text-white text-right" data-testid="setting-tr-hero-subtitle" />
              </div>
            </div>

            {/* Training Services */}
            <div className="bg-[#161618] border border-white/5 rounded-xl p-6 space-y-4">
              <div className="flex items-center justify-between border-b border-white/5 pb-2">
                <h3 className="text-white font-bold font-['Zain'] text-sm">البرامج التدريبية</h3>
                <Button size="sm" variant="outline" className="border-white/10 text-white h-7 text-xs" data-testid="add-tr-service-btn"
                  onClick={() => setForm({ ...form, tr_training_services: [...(form.tr_training_services || []), { title: "", desc: "" }] })}>
                  <Plus size={12} className="me-1" /> إضافة
                </Button>
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1.5 block">عنوان القسم</label>
                <Input value={form.tr_training_title || ""} onChange={e => setForm({ ...form, tr_training_title: e.target.value })} className="bg-background border-white/10 text-white text-right" data-testid="setting-tr-training-title" />
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1.5 block">وصف القسم</label>
                <Input value={form.tr_training_subtitle || ""} onChange={e => setForm({ ...form, tr_training_subtitle: e.target.value })} className="bg-background border-white/10 text-white text-right" data-testid="setting-tr-training-subtitle" />
              </div>
              <div className="space-y-3">
                {(form.tr_training_services || []).map((s, i) => (
                  <div key={i} className="flex gap-2 items-start bg-background rounded-lg p-3 border border-white/5">
                    <div className="flex-1 space-y-2">
                      <Input value={s.title} onChange={e => { const arr = [...form.tr_training_services]; arr[i] = { ...arr[i], title: e.target.value }; setForm({ ...form, tr_training_services: arr }); }}
                        placeholder="العنوان" className="bg-transparent border-white/10 text-white text-right h-8 text-xs" data-testid={`tr-service-title-${i}`} />
                      <Input value={s.desc} onChange={e => { const arr = [...form.tr_training_services]; arr[i] = { ...arr[i], desc: e.target.value }; setForm({ ...form, tr_training_services: arr }); }}
                        placeholder="الوصف" className="bg-transparent border-white/10 text-white text-right h-8 text-xs" data-testid={`tr-service-desc-${i}`} />
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-500 hover:text-red-400 flex-shrink-0 mt-1"
                      onClick={() => setForm({ ...form, tr_training_services: form.tr_training_services.filter((_, idx) => idx !== i) })} data-testid={`remove-tr-service-${i}`}>
                      <Trash2 size={13} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="bg-[#161618] border border-white/5 rounded-xl p-6 space-y-4">
              <h3 className="text-white font-bold font-['Zain'] text-sm border-b border-white/5 pb-2">قسم الدعوة للتسجيل</h3>
              <div>
                <label className="text-zinc-400 text-xs mb-1.5 block">عنوان الدعوة</label>
                <Input value={form.tr_cta_title || ""} onChange={e => setForm({ ...form, tr_cta_title: e.target.value })} className="bg-background border-white/10 text-white text-right" data-testid="setting-tr-cta-title" />
              </div>
            </div>

            {/* Why Join Features */}
            <div className="bg-[#161618] border border-white/5 rounded-xl p-6 space-y-4">
              <div className="flex items-center justify-between border-b border-white/5 pb-2">
                <h3 className="text-white font-bold font-['Zain'] text-sm">مميزات الانضمام</h3>
                <Button size="sm" variant="outline" className="border-white/10 text-white h-7 text-xs" data-testid="add-tr-feature-btn"
                  onClick={() => setForm({ ...form, tr_why_features: [...(form.tr_why_features || []), { title: "", desc: "" }] })}>
                  <Plus size={12} className="me-1" /> إضافة
                </Button>
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1.5 block">عنوان القسم</label>
                <Input value={form.tr_why_title || ""} onChange={e => setForm({ ...form, tr_why_title: e.target.value })} className="bg-background border-white/10 text-white text-right" data-testid="setting-tr-why-title" />
              </div>
              <div className="space-y-3">
                {(form.tr_why_features || []).map((f, i) => (
                  <div key={i} className="flex gap-2 items-start bg-background rounded-lg p-3 border border-white/5">
                    <div className="flex-1 space-y-2">
                      <Input value={f.title} onChange={e => { const arr = [...form.tr_why_features]; arr[i] = { ...arr[i], title: e.target.value }; setForm({ ...form, tr_why_features: arr }); }}
                        placeholder="العنوان" className="bg-transparent border-white/10 text-white text-right h-8 text-xs" data-testid={`tr-feature-title-${i}`} />
                      <Input value={f.desc} onChange={e => { const arr = [...form.tr_why_features]; arr[i] = { ...arr[i], desc: e.target.value }; setForm({ ...form, tr_why_features: arr }); }}
                        placeholder="الوصف" className="bg-transparent border-white/10 text-white text-right h-8 text-xs" data-testid={`tr-feature-desc-${i}`} />
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-500 hover:text-red-400 flex-shrink-0 mt-1"
                      onClick={() => setForm({ ...form, tr_why_features: form.tr_why_features.filter((_, idx) => idx !== i) })} data-testid={`remove-tr-feature-${i}`}>
                      <Trash2 size={13} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Jobs Section Title */}
            <div className="bg-[#161618] border border-white/5 rounded-xl p-6 space-y-4">
              <h3 className="text-white font-bold font-['Zain'] text-sm border-b border-white/5 pb-2">قسم الوظائف</h3>
              <div>
                <label className="text-zinc-400 text-xs mb-1.5 block">عنوان قسم الوظائف</label>
                <Input value={form.tr_jobs_title || ""} onChange={e => setForm({ ...form, tr_jobs_title: e.target.value })} className="bg-background border-white/10 text-white text-right" data-testid="setting-tr-jobs-title" />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Advanced */}
        <TabsContent value="advanced">
          <div className="bg-[#161618] border border-white/5 rounded-xl p-6 space-y-8">

            {/* Live Preview Banner */}
            <div className="rounded-2xl overflow-hidden border border-white/10" data-testid="live-preview-banner">
              <div className="relative bg-[#0a0a0b] p-8 md:p-12">
                <div className="absolute inset-0 opacity-20"
                  style={{ background: `radial-gradient(circle at top right, ${form.primary_color}30, transparent 60%), radial-gradient(circle at bottom left, ${form.gradient_color || form.primary_color}20, transparent 60%)` }} />
                <div className="relative z-10 text-center">
                  <p className="text-xs text-zinc-500 mb-3">معاينة مباشرة</p>
                  <h3 className="text-3xl md:text-5xl font-black font-['Zain'] text-white mb-2">نصنع</h3>
                  <h3 className="text-3xl md:text-5xl font-black font-['Zain'] mb-4"
                    style={{
                      background: `linear-gradient(135deg, ${form.primary_color}, ${form.gradient_color || form.primary_color})`,
                      WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                      paddingBottom: "0.15em", display: "inline-block", lineHeight: 1.4
                    }}>
                    محتوى يمثلنا
                  </h3>
                  <p className="text-zinc-400 text-sm mb-6">نحوّل الأفكار إلى تجارب مؤثرة</p>
                  <div className="flex gap-3 justify-center flex-wrap">
                    <button className="px-6 py-2.5 rounded-xl text-white text-sm font-medium" style={{ backgroundColor: form.primary_color }}>
                      ابدأ مشروعك الآن
                    </button>
                    <button className="px-6 py-2.5 rounded-xl text-sm font-medium border" style={{ borderColor: `${form.primary_color}40`, color: form.primary_color }}>
                      شاهد أعمالنا
                    </button>
                  </div>
                </div>
              </div>
              {/* Light mode preview strip */}
              <div className="bg-white p-6 flex items-center justify-between">
                <div className="flex gap-3 items-center">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${form.primary_color}15` }}>
                    <Palette size={18} style={{ color: form.primary_color }} />
                  </div>
                  <div>
                    <p className="text-gray-900 text-sm font-bold">{form.site_name || "رؤية"}</p>
                    <p className="text-gray-500 text-xs">معاينة الوضع النهاري</p>
                  </div>
                </div>
                <span className="text-sm font-bold px-4 py-1.5 rounded-lg"
                  style={{
                    background: `linear-gradient(135deg, ${form.primary_color}, ${form.gradient_color || form.primary_color})`,
                    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent"
                  }}>
                  نص متدرج
                </span>
              </div>
            </div>

            {/* Color Controls Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Primary Color */}
              <div>
                <label className="text-zinc-400 text-sm mb-3 flex items-center gap-2"><Palette size={14} /> اللون الأساسي</label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {[
                    { hex: "#CB360F", name: "أحمر" }, { hex: "#E54D2E", name: "برتقالي" },
                    { hex: "#0EA5E9", name: "سماوي" }, { hex: "#2563EB", name: "أزرق" },
                    { hex: "#7C3AED", name: "بنفسجي" }, { hex: "#059669", name: "أخضر" },
                    { hex: "#D97706", name: "ذهبي" }, { hex: "#DC2626", name: "أحمر فاقع" },
                    { hex: "#0891B2", name: "تركواز" }, { hex: "#4F46E5", name: "نيلي" },
                    { hex: "#DB2777", name: "وردي" }, { hex: "#EA580C", name: "برتقالي غامق" },
                  ].map((c) => (
                    <button key={c.hex} type="button" onClick={() => setForm({ ...form, primary_color: c.hex })}
                      className={`w-8 h-8 rounded-lg border-2 transition-all hover:scale-110 ${form.primary_color === c.hex ? "border-white shadow-lg" : "border-transparent"}`}
                      style={{ backgroundColor: c.hex }} title={c.name} data-testid={`color-preset-${c.hex.replace("#", "")}`}
                    >
                      {form.primary_color === c.hex && <Check size={12} className="text-white m-auto" />}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2 items-center">
                  <input type="color" value={form.primary_color} onChange={e => setForm({ ...form, primary_color: e.target.value })}
                    className="w-10 h-10 rounded-lg cursor-pointer border-2 border-white/10 bg-transparent" data-testid="setting-color-picker" />
                  <Input value={form.primary_color} onChange={e => setForm({ ...form, primary_color: e.target.value })}
                    className="bg-background border-white/10 text-white w-28 font-mono text-xs" dir="ltr" data-testid="setting-color" />
                </div>
              </div>

              {/* Gradient Color */}
              <div>
                <label className="text-zinc-400 text-sm mb-3 flex items-center gap-2"><Palette size={14} /> لون التدرج (الثاني)</label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {[
                    { hex: "#7C3AED", name: "بنفسجي" }, { hex: "#6366F1", name: "نيلي" },
                    { hex: "#EC4899", name: "وردي" }, { hex: "#F59E0B", name: "ذهبي" },
                    { hex: "#EF4444", name: "أحمر" }, { hex: "#06B6D4", name: "سماوي" },
                    { hex: "#10B981", name: "أخضر" }, { hex: "#F97316", name: "برتقالي" },
                    { hex: "#8B5CF6", name: "بنفسجي فاتح" }, { hex: "#3B82F6", name: "أزرق" },
                    { hex: "#E11D48", name: "أحمر وردي" }, { hex: "#14B8A6", name: "تركواز" },
                  ].map((c) => (
                    <button key={c.hex} type="button" onClick={() => setForm({ ...form, gradient_color: c.hex })}
                      className={`w-8 h-8 rounded-lg border-2 transition-all hover:scale-110 ${form.gradient_color === c.hex ? "border-white shadow-lg" : "border-transparent"}`}
                      style={{ backgroundColor: c.hex }} title={c.name} data-testid={`gradient-preset-${c.hex.replace("#", "")}`}
                    >
                      {form.gradient_color === c.hex && <Check size={12} className="text-white m-auto" />}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2 items-center">
                  <input type="color" value={form.gradient_color || "#7C3AED"} onChange={e => setForm({ ...form, gradient_color: e.target.value })}
                    className="w-10 h-10 rounded-lg cursor-pointer border-2 border-white/10 bg-transparent" data-testid="setting-gradient-picker" />
                  <Input value={form.gradient_color || ""} onChange={e => setForm({ ...form, gradient_color: e.target.value })}
                    className="bg-background border-white/10 text-white w-28 font-mono text-xs" dir="ltr" data-testid="setting-gradient-color" />
                </div>
              </div>
            </div>

            {/* Gradient Bar Preview */}
            <div className="h-3 rounded-full" style={{ background: `linear-gradient(to left, ${form.primary_color}, ${form.gradient_color || form.primary_color})` }} data-testid="gradient-bar-preview" />

            {/* Maintenance */}
            <div className="flex items-center justify-between bg-background rounded-lg p-4 border border-white/10">
              <div>
                <p className="text-white text-sm font-medium">وضع الصيانة</p>
                <p className="text-zinc-500 text-xs">تعطيل الموقع مؤقتًا وعرض رسالة صيانة</p>
              </div>
              <Switch checked={form.maintenance_mode} onCheckedChange={v => setForm({ ...form, maintenance_mode: v })} data-testid="setting-maintenance" />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// --- Main Dashboard ---
export default function AdminDashboard() {
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "overview";
  const updateGlobalSettings = useSettingsUpdater();

  const [stats, setStats] = useState({ portfolio_count: 0, courses_count: 0, testimonials_count: 0, partners_count: 0, messages_count: 0, unread_messages: 0, jobs_count: 0, applications_count: 0, employees_count: 0, influencers_count: 0 });
  const [portfolio, setPortfolio] = useState([]);
  const [courses, setCourses] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [partners, setPartners] = useState([]);
  const [messages, setMessages] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [influencers, setInfluencers] = useState([]);
  const [settings, setSettings] = useState({
    site_name: "رؤية", site_description: "", logo_url: "", logo_dark_url: "", logo_light_url: "", phone: "", email: "", whatsapp: "",
    address: "", instagram: "", twitter: "", linkedin: "", youtube: "", tiktok: "", snapchat: "",
    meta_title: "", meta_description: "", primary_color: "#CB360F", gradient_color: "#7C3AED", footer_text: "", maintenance_mode: false,
    hero_image: "", marketing_image: "", production_image: "", training_image: "", about_image: "", about_team_image: "", recruitment_image: "",
    map_embed_url: ""
  });

  const fetchAll = async () => {
    const h = { headers: authHeaders() };
    try {
      const [s, p, c, t, pr, m, j, a, st, inf] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, h),
        axios.get(`${API}/portfolio`),
        axios.get(`${API}/courses`),
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/partners`),
        axios.get(`${API}/contact`, h),
        axios.get(`${API}/jobs`),
        axios.get(`${API}/jobs/applications/all`, h),
        axios.get(`${API}/settings`),
        axios.get(`${API}/influencers`),
      ]);
      setStats(s.data); setPortfolio(p.data); setCourses(c.data); setTestimonials(t.data);
      setPartners(pr.data); setMessages(m.data); setJobs(j.data); setApplications(a.data); setSettings(st.data); setInfluencers(inf.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleCrud = async (endpoint, action, id, data) => {
    const h = { headers: authHeaders() };
    try {
      if (action === "add") await axios.post(`${API}/${endpoint}`, data, h);
      else if (action === "edit") await axios.put(`${API}/${endpoint}/${id}`, data, h);
      else if (action === "delete") {
        if (!window.confirm("هل أنت متأكد من الحذف؟")) return;
        await axios.delete(`${API}/${endpoint}/${id}`, h);
      }
      toast.success("تمت العملية بنجاح");
      fetchAll();
    } catch { toast.error("حدث خطأ"); }
  };

  const markRead = async (id) => {
    try { await axios.put(`${API}/contact/${id}/read`, {}, { headers: authHeaders() }); fetchAll(); } catch {}
  };

  const saveSettings = async (data) => {
    try {
      await axios.put(`${API}/settings`, data, { headers: authHeaders() });
      updateGlobalSettings(data);
      toast.success("تم حفظ الإعدادات بنجاح");
      fetchAll();
    } catch { toast.error("حدث خطأ في حفظ الإعدادات"); }
  };

  const catLabel = (c) => c === "marketing" ? "تسويق" : c === "production" ? "إنتاج" : "تدريب";
  const jobTypeLabel = (t) => ({ "full-time": "كامل", "part-time": "جزئي", "remote": "عن بعد", "contract": "عقد" }[t] || t);

  const titles = {
    overview: "نظرة عامة", portfolio: "إدارة الأعمال", courses: "إدارة الدورات",
    jobs: "إدارة التوظيف", testimonials: "إدارة آراء العملاء", partners: "إدارة الشركاء",
    messages: "الرسائل", settings: "الإعدادات", employees: "إدارة الموظفين", users: "إدارة المستخدمين",
    influencers: "صناع المحتوى والمؤثرين"
  };

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-2xl font-bold font-['Zain'] text-white mb-6">{titles[tab] || "نظرة عامة"}</h1>

      {(tab === "overview" || !tab) && (
        <>
          <WelcomeBanner />
          <StatsOverview stats={stats} />
        </>
      )}

      {tab === "portfolio" && (
        <CrudSection title="الأعمال" items={portfolio}
          columns={[
            { key: "title", label: "العنوان" },
            { key: "category", label: "القسم", render: (i) => <Badge variant="outline" className="border-primary/30 text-primary">{catLabel(i.category)}</Badge> },
            { key: "client_name", label: "العميل" },
          ]}
          onAdd={(data) => handleCrud("portfolio", "add", null, data)}
          onEdit={(id, data) => handleCrud("portfolio", "edit", id, data)}
          onDelete={(id) => handleCrud("portfolio", "delete", id)}
          renderForm={(item, onSubmit) => <PortfolioForm item={item} onSubmit={onSubmit} />}
        />
      )}

      {tab === "courses" && (
        <CoursesAdminSection courses={courses} onRefresh={() => axios.get(`${API}/courses`, { headers: authHeaders() }).then(r => setCourses(r.data))} />
      )}

      {tab === "jobs" && (
        <div className="space-y-8">
          <CrudSection title="الوظائف" items={jobs}
            columns={[
              { key: "title", label: "المسمى" },
              { key: "department", label: "القسم" },
              { key: "location", label: "الموقع" },
              { key: "job_type", label: "النوع", render: (i) => jobTypeLabel(i.job_type) },
              { key: "is_active", label: "الحالة", render: (i) => (
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      await axios.patch(`${API}/jobs/${i.id}/toggle`, {}, { headers: authHeaders() });
                      toast.success(i.is_active ? "تم إخفاء الوظيفة" : "تم إظهار الوظيفة");
                      fetchAll();
                    } catch { toast.error("حدث خطأ"); }
                  }}
                  className="flex items-center gap-1.5 cursor-pointer group"
                  data-testid={`toggle-job-${i.id}`}
                >
                  {i.is_active ? <Eye size={13} className="text-green-400 group-hover:text-green-300" /> : <EyeOff size={13} className="text-red-400 group-hover:text-red-300" />}
                  <Badge className={`${i.is_active ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"} border-0 transition-colors`}>
                    {i.is_active ? "ظاهرة" : "مخفية"}
                  </Badge>
                </button>
              )},
            ]}
            onAdd={(data) => handleCrud("jobs", "add", null, data)}
            onEdit={(id, data) => handleCrud("jobs", "edit", id, data)}
            onDelete={(id) => handleCrud("jobs", "delete", id)}
            renderForm={(item, onSubmit) => <JobForm item={item} onSubmit={onSubmit} />}
          />
          <ApplicationsSection applications={applications} jobs={jobs} onDelete={(id) => handleCrud("jobs/applications", "delete", id)} />
        </div>
      )}

      {tab === "testimonials" && (
        <CrudSection title="آراء العملاء" items={testimonials}
          columns={[
            { key: "name", label: "الاسم" },
            { key: "company", label: "الشركة" },
            { key: "rating", label: "التقييم", render: (i) => <span className="text-primary">{i.rating}/5</span> },
          ]}
          onAdd={(data) => handleCrud("testimonials", "add", null, data)}
          onEdit={(id, data) => handleCrud("testimonials", "edit", id, data)}
          onDelete={(id) => handleCrud("testimonials", "delete", id)}
          renderForm={(item, onSubmit) => <TestimonialForm item={item} onSubmit={onSubmit} />}
        />
      )}

      {tab === "partners" && (
        <CrudSection title="الشركاء" items={partners}
          columns={[
            { key: "name", label: "الاسم" },
            { key: "website", label: "الموقع" },
          ]}
          onAdd={(data) => handleCrud("partners", "add", null, data)}
          onEdit={(id, data) => handleCrud("partners", "edit", id, data)}
          onDelete={(id) => handleCrud("partners", "delete", id)}
          renderForm={(item, onSubmit) => <PartnerForm item={item} onSubmit={onSubmit} />}
        />
      )}

      {tab === "influencers" && (
        <CrudSection title="صناع المحتوى والمؤثرين" items={influencers}
          columns={[
            { key: "name", label: "الاسم" },
            { key: "category", label: "التصنيف", render: (i) => <Badge variant="outline" className="border-primary/30 text-primary text-xs">{i.category}</Badge> },
            { key: "followers_count", label: "المتابعين" },
            { key: "is_featured", label: "مميز", render: (i) => <Badge className={`${i.is_featured ? "bg-primary/10 text-primary" : "bg-zinc-500/10 text-zinc-500"} border-0 text-xs`}>{i.is_featured ? "مميز" : "عادي"}</Badge> },
            { key: "is_active", label: "الحالة", render: (i) => (
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await axios.patch(`${API}/influencers/${i.id}/toggle`, {}, { headers: authHeaders() });
                    toast.success(i.is_active ? "تم الإخفاء" : "تم الإظهار");
                    fetchAll();
                  } catch { toast.error("حدث خطأ"); }
                }}
                className="flex items-center gap-1.5 cursor-pointer group"
                data-testid={`toggle-influencer-${i.id}`}
              >
                {i.is_active ? <Eye size={13} className="text-green-400" /> : <EyeOff size={13} className="text-red-400" />}
                <Badge className={`${i.is_active ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"} border-0`}>
                  {i.is_active ? "ظاهر" : "مخفي"}
                </Badge>
              </button>
            )},
          ]}
          onAdd={(data) => handleCrud("influencers", "add", null, data)}
          onEdit={(id, data) => handleCrud("influencers", "edit", id, data)}
          onDelete={(id) => handleCrud("influencers", "delete", id)}
          renderForm={(item, onSubmit) => <InfluencerForm item={item} onSubmit={onSubmit} />}
        />
      )}

      {tab === "messages" && (
        <MessagesSection messages={messages} onMarkRead={markRead} onDelete={(id) => handleCrud("contact", "delete", id)} />
      )}

      {tab === "settings" && (
        <SettingsSection settings={settings} onSave={saveSettings} />
      )}

      {tab === "employees" && (
        <EmployeesSection />
      )}

      {tab === "users" && (
        <UsersSection />
      )}
    </div>
  );
}
